import { MissionSessionLight, MissionSummary } from '@wwf/sdk';
import {
    HorizontalScrollRow,
    type HorizontalScrollRowHandle,
} from '@app/misc/HorizontalScrollRow';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { MissionSessionParticipantPanel } from './MissionSessionParticipantPanel';

const sortMissionSessions = (
    sessions: MissionSessionLight[]
): MissionSessionLight[] =>
    [...sessions].sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) {
            return a.sortOrder - b.sortOrder;
        }
        return String(a.id).localeCompare(String(b.id));
    });

export const MissionSessionsTab = ({ record }: { record: MissionSummary }) => {
    const sessions = useMemo(
        () => sortMissionSessions(record.sessions ?? []),
        [record.sessions]
    );
    const [selectedSessionId, setSelectedSessionId] = useState<string | false>(
        false
    );

    useEffect(() => {
        if (!sessions.length) {
            setSelectedSessionId(false);
            return;
        }

        if (
            !selectedSessionId ||
            !sessions.some(session => session.id === selectedSessionId)
        ) {
            setSelectedSessionId(sessions[0].id);
        }
    }, [sessions, selectedSessionId]);

    const selectedSession = sessions.find(
        session => session.id === selectedSessionId
    );
    const sessionTabRefs = useRef(new Map<string, HTMLButtonElement | null>());
    const tabListScrollRef = useRef<HorizontalScrollRowHandle>(null);

    useLayoutEffect(() => {
        if (typeof selectedSessionId !== 'string') return;
        const el = sessionTabRefs.current.get(selectedSessionId);
        el?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        tabListScrollRef.current?.updateScrollEdges();
    }, [selectedSessionId, sessions.length]);

    return (
        <Box
            p={2}
            sx={{
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
            }}
        >
            {!sessions.length && (
                <Typography color="textSecondary">No sessions</Typography>
            )}
            {!!sessions.length && (
                <>
                    <HorizontalScrollRow
                        ref={tabListScrollRef}
                        role="tablist"
                        ariaLabel="Mission sessions"
                        borderBottom
                        leftAriaLabel="Scroll sessions left"
                        rightAriaLabel="Scroll sessions right"
                    >
                        {sessions.map(session => {
                            const selected = session.id === selectedSessionId;
                            return (
                                <ButtonBase
                                    key={session.id}
                                    ref={el => {
                                        if (el) {
                                            sessionTabRefs.current.set(
                                                session.id,
                                                el
                                            );
                                        } else {
                                            sessionTabRefs.current.delete(
                                                session.id
                                            );
                                        }
                                    }}
                                    role="tab"
                                    id={`mission-session-tab-${session.id}`}
                                    aria-selected={selected}
                                    tabIndex={selected ? 0 : -1}
                                    onClick={() =>
                                        setSelectedSessionId(session.id)
                                    }
                                    sx={theme => ({
                                        flex: '0 0 auto',
                                        minWidth: 220,
                                        maxWidth: 300,
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-start',
                                        textAlign: 'left',
                                        px: 2,
                                        py: 1,
                                        borderRadius: 0,
                                        borderBottom: 2,
                                        borderColor: selected
                                            ? 'primary.main'
                                            : 'transparent',
                                        color: selected
                                            ? 'primary.main'
                                            : 'text.primary',
                                        mb: '-1px',
                                        transition: theme.transitions.create(
                                            [
                                                'border-color',
                                                'color',
                                                'background-color',
                                            ],
                                            {
                                                duration:
                                                    theme.transitions.duration
                                                        .shortest,
                                            }
                                        ),
                                        ...(!selected && {
                                            '&:hover': {
                                                backgroundColor:
                                                    theme.palette.action.hover,
                                            },
                                        }),
                                    })}
                                >
                                    <SessionTabLabel session={session} />
                                </ButtonBase>
                            );
                        })}
                    </HorizontalScrollRow>

                    {selectedSession && (
                        <MissionSessionParticipantPanel
                            record={record}
                            session={selectedSession}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

const formatSessionDate = (value: string | null) => {
    if (!value) return null;
    try {
        return format(new Date(value), 'PPp');
    } catch {
        return value;
    }
};

const formatParticipantCount = (count: number) =>
    count === 1 ? '1 participant' : `${count} participants`;

const formatSessionPeriod = (session: MissionSessionLight) => {
    const start = formatSessionDate(session.startDate);
    const end = formatSessionDate(session.endDate);

    if (start && end) return `${start}\n → ${end}`;
    return start ?? end ?? session.label ?? 'No date';
};

const SessionTabLabel = ({ session }: { session: MissionSessionLight }) => (
    <Stack
        alignItems="flex-start"
        textTransform="none"
        sx={{ maxWidth: 200, minWidth: 0 }}
    >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {formatSessionPeriod(session)}
        </Typography>
        {session.label && (
            <Typography variant="caption" color="text.secondary">
                {session.label}
            </Typography>
        )}
        <Typography variant="caption" color="text.secondary">
            {formatParticipantCount(session.participantIds?.length ?? 0)}
        </Typography>
    </Stack>
);
